import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { ScreenTransition } from '@/components/ScreenTransition'
import { ProgressBar } from '@/components/ProgressBar'
import { ShapeCard } from '@/components/ShapeCard'
import { Carousel } from '@/components/Carousel'
import { BODY_SHAPES } from '@/data/bodyShapes'
import { SHAPE_SCREEN_QUOTE } from '@/data/quotes'
import { useQuiz } from '@/state/QuizContext'
import { pingScreen } from '@/lib/ping'

/**
 * Screen 3 - Body shape. Single-select card grid with placeholder imagery.
 *
 * Validation: one shape required before Next is enabled. If the user already
 * chose a shape earlier (sessionStorage restore), their selection pre-loads.
 *
 * Gatekeeping: if the user hasn't answered Screen 2, we redirect them back.
 * This matters if they reach this URL directly via history/back-forward cache.
 */
export function ShapeScreen() {
  const { activity, bodyShape, setBodyShape, painPoints } = useQuiz()
  const navigate = useNavigate()

  useEffect(() => { pingScreen('shape') }, [])

  // Gates: can't answer shape before activity + pain points
  if (!activity)               return <Navigate to="/questions/1" replace />
  if (painPoints.length === 0) return <Navigate to="/questions/2" replace />

  const canContinue = bodyShape !== null

  function handleNext() {
    if (!canContinue) return
    navigate('/questions/4')
  }

  return (
    <ScreenTransition className="fq-question">
      <div className="fq-q-inner">
        <ProgressBar current={3} total={4} />

        <header className="fq-q-header">
          <p className="fq-eyebrow">Question 3 of 4</p>
          <h2 className="fq-h2">Which of these<br />sounds <span className="fq-accent">most like you?</span></h2>
          <p className="fq-lede">
            Your body is yours - this helps us start with the right style. Pick whichever
            shape feels closest, but don't worry if it's not an exact match.
          </p>
        </header>

        <Carousel>
          <div
            className="fq-shape-grid"
            role="radiogroup"
            aria-label="Body shape"
          >
            {BODY_SHAPES.map((option) => (
              <ShapeCard
                key={option.id}
                option={option}
                selected={bodyShape === option.id}
                onSelect={() => setBodyShape(option.id)}
              />
            ))}
          </div>
        </Carousel>

        <div className="fq-quote-row">
          <figure className="fq-quote">
            <blockquote>"{SHAPE_SCREEN_QUOTE.text}" <span className="fq-quote-attr">- {SHAPE_SCREEN_QUOTE.attribution}</span></blockquote>
          </figure>
          <div className="fq-actions">
            <Link to="/questions/2" className="fq-back">Back</Link>
            <button
              type="button"
              className="fq-cta"
              onClick={handleNext}
              disabled={!canContinue}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </ScreenTransition>
  )
}
